import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { BrandKitForm } from '../BrandKit/BrandKitForm/BrandKitForm'
import { BrandKitFormProvider } from '../BrandKit/BrandKitForm/subcomponents/BrandKitFormProvider'
import { Button } from '@dtx-company/design-system/src/components/Button/Button'
import { Container } from '@dtx-company/design-system/src/components/Container/Container'
import { DHUserMode } from '../../../types/design-huddle.types'
import { DesignHuddleProvider } from '../DesignHuddle/context'
import { Divider } from '@dtx-company/design-system/src/components/Divider/Divider'
import { FC, useState } from 'react'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { GradientSpinner } from '@dtx-company/shared-components/src/components/atoms/GradientSpinner/GradientSpinner'
import { Grid } from '@dtx-company/design-system/src/components/Grid/Grid'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { MobileFiltersModal as MobileFiltersModalV2 } from '../PrintStoreTemplatesFilters/MobileFiltersModal'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { PrintStoreTemplatesFilter } from '../PrintStoreTemplatesFilters/PrintStoreTemplatesFilter'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { TemplateCard } from './TemplateCard'
import { TemplateFilterSection } from '../PrintStoreTemplatesFilters/types'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { Typography } from '@dtx-company/design-system/src/components/Typography/Typography'
import { setSelectedFlowcode } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { theme } from '@dtx-company/design-system/src/theme'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDispatch } from 'react-redux'
import { useMediaQuery } from '@mui/material'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils/modal'
import { usePrintStoreTemplatesFilters } from '../PrintStoreTemplatesFilters/hooks'
import { useRestoreTemplatesPageScrollPosition } from '../hooks/useRestoreTemplatesPageScrollPosition'
import { useTemplatesListState } from './TemplatesList.hooks'
import SearchIcon from '@mui/icons-material/Search'
import dynamic from 'next/dynamic'

const PrintStoreTemplatesSignUpModal = dynamic<Record<string, unknown>>(() =>
  import('../SignUpModal/SignUpModal').then(mod => mod.PrintStoreTemplatesSignUpModal)
)

export const LoadingSpinner = (): JSX.Element => {
  return (
    <Box
      sx={{ display: 'block', width: '45px', height: '45px', margin: '20px auto' }}
      data-testid="skeleton-loader"
    >
      <GradientSpinner />
    </Box>
  )
}

export const TemplatesList: FC = () => {
  const [predesignedFetchLimit, setPredesignedFetchLimit] = useState(0)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [redirectPath, setRedirectPath] = useState('')
  const { modalOpen } = useModal(ModalTypes.TEMPLATES_SIGN_UP_MODAL)
  const {
    clearTemplates,
    handleLoadMore,
    templates,
    hasMore,
    isLoading,
    isFetching,
    hasTemplates,
    hasPredesignedTemplates,
    predesignedTemplatesLength
  } = useTemplatesListState({
    predesignedFetchLimit
  })

  useRestoreTemplatesPageScrollPosition({
    templatesLength: predesignedTemplatesLength,
    onLoad: setPredesignedFetchLimit,
    shouldRestoreScroll: !isLoading && !isFetching && hasPredesignedTemplates
  })

  const dispatch = useDispatch()
  const { selectedTemplateFlowcode, selectedFlowcode } = useCodeSelector(
    (state: RootCodeState) => state.printStore
  )

  const { onResetHandler: resetVerticalCategories } = usePrintStoreTemplatesFilters({
    sectionName: TemplateFilterSection.Vertical,
    clearTemplates
  })
  const { onResetHandler: resetUseCaseCategories } = usePrintStoreTemplatesFilters({
    sectionName: TemplateFilterSection.UseCase,
    clearTemplates
  })

  const isXlBreakpoint = useMediaQuery(theme.breakpoints.up('xl'))

  //to set selected code on editor incase an another code was selected in an in-progress project
  // before coming to the templates list page.
  // revist this logic when working on SC-71104
  const handleCardClick = (): void => {
    if (selectedTemplateFlowcode && selectedFlowcode?.id !== selectedTemplateFlowcode?.id) {
      dispatch(setSelectedFlowcode(selectedTemplateFlowcode))
    }
  }

  return (
    <>
      <DesignHuddleProvider mode={DHUserMode.VISITOR}>
        <Container maxWidth={isXlBreakpoint ? 'xl' : 'lg'}>
          <Box
            sx={theme => ({
              margin: { xs: theme.spacing(6, 1.5), md: theme.spacing(0, 0, 6) }
            })}
            role="group"
            aria-labelledby="brand-kit-header"
          >
            <Box sx={{ mb: 5.5 }}>
              <Typography variant="subtitleMedium" component="h2" id="brand-kit-header">
                Preview templates with your brand elements. Select a template to continue designing.
              </Typography>
            </Box>
            <BrandKitFormProvider>
              <BrandKitForm />
            </BrandKitFormProvider>
          </Box>
          <Divider sx={{ mb: 5.5 }} />
          <Box
            sx={theme => ({
              display: { xs: 'none', md: 'flex' },
              marginBottom: { md: theme.spacing(6) },
              flexDirection: 'row',
              columnGap: theme.spacing(4),
              width: '100%'
            })}
          >
            <PrintStoreTemplatesFilter
              sectionName={TemplateFilterSection.Vertical}
              clearTemplates={clearTemplates}
            />
            <PrintStoreTemplatesFilter
              sectionName={TemplateFilterSection.UseCase}
              clearTemplates={clearTemplates}
            />
          </Box>
          <GenericButton
            type="button"
            width="173px"
            height="40px"
            display={{ xs: 'flex', md: 'none' }}
            onClick={() => setMobileFiltersOpen(true)}
            borderColor="secondary.border"
            borderRadius="4px"
            margin={'0 auto'}
          >
            <Icon height="1rem" width="1rem" src="/icons/filter2.svg" />
            <Spacer mr="8px" />
            <Text variant="button/pageNav" color="primary.grey">
              Filters
            </Text>
          </GenericButton>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <Box
              sx={theme => ({
                flexDirection: 'column',
                width: '100%',
                textAlign: 'center',
                marginTop: { xs: theme.spacing(6), md: 0 },
                marginBottom: { xs: theme.spacing(2.5), md: 0 }
              })}
              data-testid="templates-filter-list-ctr"
            >
              {!hasTemplates && !isFetching ? (
                <Box sx={{ marginTop: '120px' }}>
                  <SearchIcon
                    sx={theme => ({
                      width: theme.spacing(10),
                      height: theme.spacing(10),
                      marginBottom: 5
                    })}
                  />
                  <Box
                    sx={theme => ({
                      marginTop: theme.spacing(2)
                    })}
                  >
                    <Typography variant="subtitleLarge" component="h3">
                      Sorry, no matches
                    </Typography>
                  </Box>
                  <Typography variant="bodyMedium" color={theme.palette.text.secondary}>
                    Try removing filters
                  </Typography>
                  <Box
                    sx={theme => ({
                      marginTop: theme.spacing(8)
                    })}
                  >
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        resetVerticalCategories()
                        resetUseCaseCategories()
                      }}
                    >
                      Remove all filters
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Grid
                  container
                  spacing={3}
                  sx={{ mt: 0, mx: 'auto', mb: 10, pl: 0, listStyleType: 'none' }}
                  component="ul"
                >
                  {templates?.map(template => (
                    <Grid
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2.4}
                      key={template.templateId}
                      component="li"
                    >
                      <TemplateCard
                        template={template}
                        loggedOutHandler={setRedirectPath}
                        onClickAuthedCard={handleCardClick}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              <Spacer mt="40px" />
              {hasMore && hasTemplates && (
                <Button variant="text" onClick={handleLoadMore} color="tertiary">
                  See more templates
                </Button>
              )}
            </Box>
          )}
        </Container>
        <MobileFiltersModalV2
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          clearTemplates={clearTemplates}
        />
      </DesignHuddleProvider>
      {modalOpen && <PrintStoreTemplatesSignUpModal redirectPath={redirectPath} />}
    </>
  )
}
